import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PackageStatusBadge } from "@/components/StatusBadge";
import { Loader2, FilePlus2 } from "lucide-react";

export default function CreateBatch() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselect = (params.get("packages") || "").split(",").filter(Boolean);

  const [batchName, setBatchName] = useState("");
  const [destination, setDestination] = useState("");
  const [route, setRoute] = useState("");
  const [dispatchDate, setDispatchDate] = useState("");
  const [notes, setNotes] = useState("");

  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set(preselect));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/warehouse/packages", { params: { status: "RECEIVED_AT_WAREHOUSE,SORTING_IN_WAREHOUSE" } })
      .then(r => setPkgs(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (pid) => {
    const s = new Set(selected);
    s.has(pid) ? s.delete(pid) : s.add(pid);
    setSelected(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!batchName.trim()) {
      toast.error("Batch name is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/warehouse/batches", {
        batch_name: batchName,
        destination_area: destination,
        route,
        dispatch_date: dispatchDate,
        notes,
        package_ids: Array.from(selected),
      });
      toast.success(`Created ${data.batch_code} with ${data.total_packages} package(s).`);
      navigate(`/warehouse/batches/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="wh-create-batch-page" className="space-y-5 max-w-4xl">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ Outbound</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Create Outbound Batch</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Pick the destination and the packages to ship together.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Batch Name *">
          <Input data-testid="wh-batch-name" value={batchName} onChange={e => setBatchName(e.target.value)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
        </Field>
        <Field label="Destination Area">
          <Input data-testid="wh-batch-destination" value={destination} onChange={e => setDestination(e.target.value)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
        </Field>
        <Field label="Route">
          <Input data-testid="wh-batch-route" value={route} onChange={e => setRoute(e.target.value)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
        </Field>
        <Field label="Dispatch Date">
          <Input data-testid="wh-batch-dispatch-date" type="date" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Notes / Instructions">
            <Textarea data-testid="wh-batch-notes" value={notes} onChange={e => setNotes(e.target.value)} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" rows={2} />
          </Field>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-600 dark:text-slate-400">Select Packages</Label>
          <div className="text-xs text-slate-500"><span className="text-cyan-300 font-mono-tech">{selected.size}</span> selected</div>
        </div>
        {loading ? (
          <div className="py-10 text-center text-slate-500">Loading packages…</div>
        ) : pkgs.length === 0 ? (
          <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-10 text-center text-slate-500">
            No sortable packages available right now.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin border border-slate-200 dark:border-slate-800 rounded-lg p-2">
            {pkgs.map(p => (
              <label
                key={p.id}
                data-testid={`wh-batch-pkg-${p.package_id}`}
                className={`flex items-center gap-3 p-2 rounded cursor-pointer ${selected.has(p.package_id) ? "bg-cyan-500/10" : "hover:bg-slate-100 hover:dark:bg-slate-900"}`}
              >
                <Checkbox
                  checked={selected.has(p.package_id)}
                  onCheckedChange={() => toggle(p.package_id)}
                  className="border-slate-300 dark:border-slate-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <div className="font-mono-tech text-xs text-cyan-300">{p.qr_code_id}</div>
                    <PackageStatusBadge status={p.status} />
                  </div>
                  <div className="font-display text-sm font-semibold truncate">{p.package_name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate">{p.store_name} → {p.receiver_name}</div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/warehouse/batches")}
          className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          data-testid="wh-batch-submit"
          disabled={saving}
          className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FilePlus2 className="w-4 h-4 mr-2" />}
          Create Batch
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">{label}</Label>
      {children}
    </div>
  );
}
